import { useCityTermLocalStorage } from "Composables/useCityTermLocalStorage";
import { useCurrentSite } from "Composables/useCurrentSite";
import { captureTryCatchFetchError } from "Main/fetch-helper";
import { fetchAdvisorSchedules } from "./advisor-schedules-request";
import {
  readAvailability,
  type TAvailability,
  writeAvailability,
} from "./availability-cache";
import { getFirstAvailableTimeFor } from "./first-available-time";

function resolveContext() {
  const { currentSiteUpperCase } = useCurrentSite();
  const { getCityTermFromLocalStorage } = useCityTermLocalStorage();
  const cityTerm = getCityTermFromLocalStorage();

  return {
    city: cityTerm ? cityTerm.name : "",
    citySlug: cityTerm ? cityTerm.slug : "",
    site: currentSiteUpperCase.value,
  };
}

/**
 * What the footer paints before anything is fetched, so the line is there on
 * first render rather than arriving late and shifting the bar.
 */
export function readCachedAvailability(role: string): TAvailability | null {
  const { citySlug, site } = resolveContext();

  return readAvailability(site, role, citySlug);
}

/**
 * The soonest time any advisor on the listing can be seen, from cache while it
 * is fresh and from the advisors endpoint once it is not. Null when the
 * request failed and the footer should keep whatever it is showing.
 */
export async function loadAvailability(
  role: string
): Promise<TAvailability | null> {
  const { city, citySlug, site } = resolveContext();
  const cached = readAvailability(site, role, citySlug);

  if (cached) {
    return cached;
  }

  try {
    const advisors = await fetchAdvisorSchedules(role, citySlug);
    const availability = { city, time: getFirstAvailableTimeFor(advisors) };

    // Nothing worth caching, and writing it would hide a good earlier answer
    // behind a fresh timestamp.
    if (availability.time || availability.city) {
      writeAvailability(site, role, citySlug, availability);
    }

    return availability;
  } catch (error) {
    captureTryCatchFetchError(error as Error, "stickyCtaFooterAvailability");

    return null;
  }
}

/**
 * Off the critical path: the line is worth a few hundred milliseconds, not a
 * request competing with the page the visitor came for.
 */
export function whenIdle(run: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run, { timeout: 3000 });

    return;
  }

  window.setTimeout(run, 1000);
}
